'use client'

import { useRef } from 'react'

import {
  getSearchStartData,
  getSearchStartDataFromUrl,
  setSearchSessionData,
} from '@marketplace-web/search/search'
import { Response } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { SortByOption, GetCatalogItemsResp } from '@marketplace-web/domain/catalog-data'

import {
  endOfListEvent,
  userAppliedOrderEvent,
  userFilterItemsEvent,
  userPaginateCatalogEvent,
  useCatalogContext,
  SearchSessionReducerAction,
} from '@marketplace-web/domain/catalog-page-data'

const useItemsTracking = () => {
  const { track } = useTracking()
  const {
    state: {
      filters,
      dynamicFilters: { selectedDynamicFilters },
    },
    dispatch,
  } = useCatalogContext()
  const { search_text, search_by_image_uuid: searchByImageUuid } = useLocation().searchParams

  const prevPage = useRef(0)

  const trackEndOfList = (res: Response<GetCatalogItemsResp>) => {
    track(
      endOfListEvent({
        itemCount: res.pagination.total_entries,
        page: res.pagination.current_page,
        perPage: res.pagination.per_page,
      }),
    )
  }

  const trackUserPaginateCatalog = (res: Response<GetCatalogItemsResp>) => {
    track(
      userPaginateCatalogEvent({
        currentPage: res.pagination.current_page,
        globalSearchSessionId: res.search_tracking_params.global_search_session_id || null,
        globalCatalogBrowseSessionId:
          res.search_tracking_params.global_catalog_browse_session_id || null,
        searchCorrelationId: res.search_tracking_params.search_correlation_id,
        searchSessionId: res.search_tracking_params.search_session_id,
      }),
    )
  }

  const trackUserAppliedOrder = (res: Response<GetCatalogItemsResp>) => {
    if (!filters.sortBy) return
    track(
      userAppliedOrderEvent({
        order: filters.sortBy,
        globalSearchSessionId: res.search_tracking_params.global_search_session_id || null,
        searchSessionId: res.search_tracking_params.search_session_id,
      }),
    )
  }

  const trackUserFilterItems = (res: Response<GetCatalogItemsResp>) => {
    const { searchStartId: startId, searchStartType: startType } = getSearchStartData()
    const { searchStartId: startIdFromUrl, searchStartType: startTypeFromUrl } =
      getSearchStartDataFromUrl()

    const globalCatalogBrowseSessionId = res.search_tracking_params.global_catalog_browse_session_id
    const searchStartId = startId || startIdFromUrl
    const searchStartType = startType || startTypeFromUrl

    track(
      userFilterItemsEvent({
        filters,
        searchQuery: search_text ? String(search_text) : null,
        searchStartId,
        searchStartType,
        selectedDynamicFilters,
        itemCount: res.pagination.total_entries,
        globalSearchSessionId: res.search_tracking_params.global_search_session_id || null,
        searchCorrelationId: res.search_tracking_params.search_correlation_id,
        globalCatalogBrowseSessionId,
        searchSessionId: res.search_tracking_params.search_session_id,
        searchByImageUuid: searchByImageUuid ? String(searchByImageUuid) : null,
      }),
    )
  }

  const onItemsSuccess = (res: Response<GetCatalogItemsResp>) => {
    const globalCatalogBrowseSessionId = res.search_tracking_params.global_catalog_browse_session_id

    // filter items called when items succeeds in same page
    if (!prevPage.current || prevPage.current === res.pagination.current_page) {
      trackUserFilterItems(res)
    } else {
      // page change
      trackUserPaginateCatalog(res)
    }

    if (filters.sortBy && filters.sortBy !== SortByOption.Relevance) {
      trackUserAppliedOrder(res)
    }

    if (res.pagination.current_page >= res.pagination.total_pages) {
      trackEndOfList(res)
    }

    const payload = {
      searchCorrelationId: res.search_tracking_params.search_correlation_id,
      globalSearchSessionId: res.search_tracking_params.global_search_session_id,
      searchSessionId: res.search_tracking_params.search_session_id,
      globalCatalogBrowseSessionId,
    }

    setSearchSessionData({
      searchSessionId: res.search_tracking_params.search_session_id,
      globalSearchSessionId: res.search_tracking_params.global_search_session_id,
      globalCatalogBrowseSessionId,
    })

    dispatch({
      type: SearchSessionReducerAction.SET_SEARCH_SESSION_DATA,
      payload,
    })

    prevPage.current = res.pagination.current_page
  }

  return { onItemsSuccess }
}

export default useItemsTracking
